/**
 * Deterministic fingerprinting for visualization configurations.
 *
 * Used by the dashboard query cache: a visualization's `configVersion` is a stable
 * hash of its `type` + `configurations`. The frontend sends it on every cache request;
 * beem-agent treats a mismatch as a cache miss, so editing a viz's config (chart type,
 * columns, aggregations, etc.) naturally invalidates its cached results.
 *
 * The hash must be deterministic for logically-identical configs regardless of object
 * key ordering, so we serialize with sorted keys before hashing.
 */

/**
 * JSON.stringify with recursively sorted object keys. Arrays keep their order
 * (order is semantically meaningful for things like series/columns), while object
 * keys are sorted so that `{ a: 1, b: 2 }` and `{ b: 2, a: 1 }` produce identical output.
 */
export function stableStringify(value: unknown): string {
  return JSON.stringify(sortValue(value));
}

function sortValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sortValue);
  }
  if (value !== null && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    return Object.keys(obj)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = sortValue(obj[key]);
        return acc;
      }, {});
  }
  return value;
}

/**
 * FNV-1a 32-bit hash, returned as an 8-char zero-padded hex string.
 * Chosen over crypto/md5 because it's synchronous, dependency-free, and fast —
 * this is a cache fingerprint, not a security primitive.
 *
 * Collision risk (accepted): a 32-bit hash has a ~4.3B value space, so distinct configs
 * can in principle collide. This is tolerable here because `configVersion` is only ONE
 * component of the cache key — beem-agent also keys on vizId + datasetId + datasetVersion
 * + remoteFilters (see CacheParams). A collision therefore only mis-serves when the SAME
 * visualization is edited between two configs whose hashes coincide: a per-viz birthday
 * problem over a viz's handful of edit revisions, where the collision probability is
 * negligible. The worst case is a stale result for one viz config, self-healing on the
 * next dataset-version bump or config edit — never a cross-viz or cross-dataset leak.
 * If the cache key space ever widens enough to make this material, switch to a 64-bit
 * FNV-1a variant (BigInt or hi/lo 32-bit words) rather than a 32-bit number.
 */
export function fnv1aHash(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    // hash *= 16777619, kept in 32-bit range via Math.imul
    hash = Math.imul(hash, 0x01000193);
  }
  // >>> 0 coerces to unsigned 32-bit
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * Compute the cache `configVersion` for a visualization from its type + configurations.
 * Deterministic: logically-identical inputs (regardless of key order) yield the same value.
 */
export function computeConfigVersion(type: string, configurations: unknown): string {
  return fnv1aHash(stableStringify({ type, configurations }));
}
